import { Box, Flex } from '@chakra-ui/react';
import { ReactNode, useState } from 'react';
// import { TermsBanner } from '~/components/ui//banners/TermsBanner';
import { LoanContactBanner } from '~/components/ui/banners/LoanContactBanner';
import { ROUTES } from '@habity/constants';
import { useUser } from '~/hooks/useUser';
import { useUIStore } from '~/store';
import { BottomNavigation } from './BottomNavigation';
import { MobileHeader } from './Header';
import { usePathUtils } from '~/hooks/usePathUtils';
import { MobileMenu } from './MobileMenu';
import { useSearchParams } from 'next/navigation';
import { isBroker } from '~/lib/roles';

type LayoutProps = {
  children: ReactNode;
};

const MobileLayout = ({ children }: LayoutProps) => {
  const { isPath, isNotExactPath, isNotPath, isExactPath } = usePathUtils();
  const modals = useUIStore((state) => state.modals);
  const setModals = useUIStore((state) => state.setModals);
  const searchParams = useSearchParams();
  const [showLoanBanner, setShowLoanBanner] = useState(true);

  const { user } = useUser();

  // Check if buyer onboarding stepper is active (past step 0)
  const isBuyerOnboardingPastStep0 =
    searchParams?.get('step') && searchParams?.get('step') !== '0';

  const hasHeader =
    isNotPath(ROUTES.SELL) &&
    isNotExactPath(ROUTES.BUY) &&
    isNotPath(ROUTES.DASHBOARD.BUYER.EDIT_PATH);

  // Show footer on all pages except:
  // - Sell intent pages (/sell/meeting, /sell/valuation, etc.) — root /sell is allowed
  // - Buy page with stepper past step 0
  // - Dashboard buyer edit path
  // - Client portal page
  // - Analytics pages
  const hasFooter =
    isExactPath(ROUTES.DASHBOARD.ACCOUNT) || // Always show footer on account page
    isExactPath(ROUTES.SELL) || // Show footer on root /sell (no intent selected)
    (isNotPath(ROUTES.SELL) &&
      isNotExactPath(ROUTES.BUY) &&
      isNotPath(ROUTES.DASHBOARD.BUYER.EDIT_PATH) &&
      isNotExactPath(ROUTES.CLIENT_PORTAL) &&
      isNotPath(ROUTES.ANALYTICS.INDEX)) ||
    (isExactPath(ROUTES.BUY) && !isBuyerOnboardingPastStep0);

  const footerOverridden =
    isPath(ROUTES.PROFILES.BROKERS) ||
    isPath(ROUTES.PROFILES.HOMES) ||
    isPath(ROUTES.PROFILES.BUYERS);

  return (
    <>
      <Flex
        direction="column"
        flex="1"
        width="100vw"
        minHeight="100vh"
        overflowX="hidden"
        overflowY="visible"
        gap="0"
        id="mobile-layout" // Needed for programmatic scroll
      >
        {hasHeader && <MobileHeader />}

        <Flex
          as="main"
          role="main"
          direction="column"
          flex="1"
          minH="0"
          overflowY="visible"
          mb={{
            mobile: hasFooter ? '70px' : '0',
            desktop: '0',
          }}
          gap="0"
        >
          {/* Fill space for header height */}
          {hasHeader && <Box h="60px" w="100vw" />}

          {children}
        </Flex>

        {hasFooter && !footerOverridden && <BottomNavigation />}

        {/* Show loan contact banner if user is logged in, not a broker, and hasn't answered */}
        {user &&
          !isBroker(user) &&
          showLoanBanner &&
          !(user as any).loan_contact_accepted_at &&
          !(user as any).loan_contact_denied_at && (
            <LoanContactBanner onAnswer={() => setShowLoanBanner(false)} />
          )}
      </Flex>

      {/* Mobile: Fullscreen Menu */}
      <MobileMenu
        isOpen={modals.mobileMenuOpen && !modals.loginOpen}
        onClose={() => setModals({ ...modals, mobileMenuOpen: false })}
      />
    </>
  );
};

export default MobileLayout;
